<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useArticleLightbox } from '@/composables/useArticleLightbox'
import ArticlePrompt from './ArticlePrompt.vue'
import { splitLabel } from '@/composables/articleLabel'

const { current, close, returnFocus, opener } = useArticleLightbox()
const dialogEl = ref<HTMLDialogElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
let htmlOverflow = ''
let pageY = 0
let anchor: HTMLElement | null = null
let anchorTop = 0

// showModal() runs only here, after a click set `current`: never during prerender.
watch(current, async (item) => {
  const dialog = dialogEl.value
  if (!dialog) return
  if (item && !dialog.open) {
    pageY = window.scrollY
    anchor = opener()
    anchorTop = anchor?.getBoundingClientRect().top ?? 0
    htmlOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    await nextTick()
    dialog.showModal()
  } else if (!item && dialog.open) {
    dialog.close()
  }
})

// Fires for every way out (Escape, the close button, a backdrop click), so the page
// scroll comes back and `current` is cleared exactly once. The reader lands where they
// opened it: the opener goes back to the same height on screen (lazy pictures above it
// may have loaded meanwhile), instantly, before focus returns.
function onClosed() {
  document.documentElement.style.overflow = htmlOverflow
  const top = anchor?.isConnected ? window.scrollY + anchor.getBoundingClientRect().top - anchorTop : pageY
  anchor = null
  window.scrollTo({ top, behavior: 'instant' })
  if (current.value) close()
  returnFocus()
}

// The dialog fills the viewport, so a click that lands outside the panel is a click
// on the backdrop; a click on the picture or the panel's text is not.
function onDialogClick(e: MouseEvent) {
  if (panelEl.value && !panelEl.value.contains(e.target as Node)) close()
}
</script>

<template>
  <dialog
    ref="dialogEl"
    class="article-lightbox fixed inset-0 m-0 h-full w-full max-h-none max-w-none overflow-y-auto bg-transparent p-0 text-surface-100 backdrop:bg-black/80"
    :aria-label="current?.kind === 'recipe' ? current.recipe.label : current?.alt"
    @click="onDialogClick"
    @close="onClosed"
  >
    <div class="flex min-h-full items-center justify-center px-4 py-16">
      <div v-if="current" ref="panelEl" class="relative w-full" :class="current.kind === 'recipe' ? 'max-w-5xl rounded-card border border-surface-700 bg-surface-900 p-4 sm:p-6' : 'max-w-fit'">
        <button
          type="button"
          aria-label="Close"
          class="absolute right-2 top-2 z-10 inline-flex items-center justify-center min-h-11 min-w-11 rounded-full border border-surface-600 bg-surface-900 text-surface-100 hover:text-primary-300"
          @click="close()"
        >
          <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
        </button>

        <figure v-if="current.kind === 'image'" class="mx-auto w-fit max-w-full">
          <img :src="current.src" :alt="current.alt" class="block max-h-[85vh] max-w-full object-contain rounded-card" />
          <figcaption v-if="current.caption" class="mt-3 text-sm text-surface-200">{{ current.caption }}</figcaption>
        </figure>

        <template v-else>
          <h2 class="pr-12 text-lg sm:text-xl font-bold text-surface-50">{{ splitLabel(current.recipe.label).lead }}<span v-if="splitLabel(current.recipe.label).rest" class="font-normal text-surface-400"> · {{ splitLabel(current.recipe.label).rest }}</span></h2>
          <div class="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
            <figure v-if="!current.recipe.output && current.recipe.inputs?.length" class="relative min-w-0 flex-1">
              <img :src="current.recipe.inputs[0].src" alt="" class="block w-full max-h-[70vh] object-contain rounded bg-black" />
              <span v-if="current.recipe.note" class="absolute left-2 top-2 rounded-full bg-surface-950/85 px-3 py-1 text-sm font-medium text-surface-50">{{ current.recipe.note }}</span>
            </figure>
            <div v-else-if="current.recipe.inputs" class="flex shrink-0 gap-3 sm:flex-col sm:w-44">
              <figure v-for="input in current.recipe.inputs" :key="input.src" class="w-36 sm:w-full">
                <img :src="input.src" alt="" class="block w-full aspect-video object-cover rounded border border-surface-600 bg-black" />
                <figcaption class="mt-1 text-xs leading-tight text-surface-400">{{ input.label }}</figcaption>
              </figure>
              <p v-if="!current.recipe.inputs.length" class="text-xs text-surface-400">No reference picture</p>
            </div>
            <svg v-if="current.recipe.inputs && current.recipe.output" class="hidden sm:block shrink-0 w-6 h-6 text-primary-300" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10h13M11 5l5 5-5 5" /></svg>
            <figure v-if="current.recipe.output" class="min-w-0 flex-1">
              <img v-if="current.recipe.output.kind === 'image'" :src="current.recipe.output.src" :alt="current.recipe.output.alt" class="block w-full max-h-[70vh] object-contain rounded bg-black" />
              <!-- Muted on open, whatever the clip is: sound is the viewer's choice. -->
              <video v-else-if="current.recipe.output.kind === 'video'" :src="current.recipe.output.src" :poster="current.recipe.output.poster" :aria-label="current.recipe.output.alt" controls muted loop autoplay playsinline class="block w-full max-h-[70vh] bg-black object-contain rounded"></video>
              <div v-else class="rounded overflow-hidden bg-surface-800">
                <img v-if="current.recipe.output.poster" :src="current.recipe.output.poster" :alt="current.recipe.output.alt" class="block w-full max-h-[60vh] object-contain" />
                <audio :src="current.recipe.output.src" :aria-label="current.recipe.output.alt" controls preload="none" class="block w-full"></audio>
              </div>
              <figcaption v-if="current.recipe.outputLabel" class="mt-1 text-xs text-surface-400">{{ current.recipe.outputLabel }}</figcaption>
            </figure>
          </div>
          <p v-if="current.recipe.note && current.recipe.output" class="mt-4 text-sm text-surface-300 leading-relaxed">{{ current.recipe.note }}</p>
          <ArticlePrompt v-if="current.recipe.prompt" class="mt-4" compact :aria-name="`${current.recipe.label} prompt`" :text="current.recipe.prompt" :focus="current.recipe.focus" />
        </template>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
@keyframes article-lightbox-in { from { opacity: 0 } to { opacity: 1 } }
.article-lightbox[open] { animation: article-lightbox-in 0.15s ease-out; }
.article-lightbox[open]::backdrop { animation: article-lightbox-in 0.15s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .article-lightbox[open],
  .article-lightbox[open]::backdrop { animation: none; }
}
</style>
